export default {
    /**
     * From Balance amount , Average rate of interest and loan term find nper(Number of Periods) value
     * invoked with three arguments: (rate, loanTerm, balanceAmount).
     *
     * @param {any} rate calculate rate value from average rate of interest.
     * @param {any} loanTerm current monthly payment input by user.
     * @param {any} balanceAmount student loan balance amount input by user.
     * @returns {number} Returns term  value
     */
    nper(rate, loanTerm, balanceAmount) {
        const denominator = loanTerm + balanceAmount * rate;
        return (Math.log(loanTerm / denominator) / Math.log(1 + rate)).toFixed(2);
    },
    /**
     * From Balance amount , Average rate of interest and loan term find term value
     * invoked with Five arguments: (balanceAmount, averageInterestRate, loanTerm, yearsTotalMonth, hundredUnits).
     *
     * @param {any} balanceAmount student loan balance amount input by user.
     * @param {any} averageInterestRate average rate of interest value input by user.
     * @param {any} loanTerm current monthly payment input by user.
     * @param {any} yearsTotalMonth static value for total month in year 12.
     * @param {any} hundredUnits static value assign 100.
     * @returns {number} Returns term  value
     */
    calculateTermsValue(balanceAmount, averageInterestRate, loanTerm, yearsTotalMonth, hundredUnits) {
        if (!balanceAmount && !averageInterestRate && !loanTerm) {
            return 0;
        }
        return this.nper(averageInterestRate / hundredUnits / yearsTotalMonth, -loanTerm, balanceAmount) / yearsTotalMonth;
    },
    /**
     * This is common logic that return calculate student loan pay off json data value as per 'pay extra per month' or 'years to payoff'
     * invoked with three arguments: (results, balanceAmount, payment).
     *
     * @param {any} results made object that pass intial input from user and some intial calculation.made object that pass intial input from user and some intial parameter that need to pass for calculation.
     * @param {any} amount student loan balance amount input by user.
     * @param {any} payment for 'pay extra per month' payment mean monthly payment value and for 'years to payoff' payment mean desiredPayment value
     * @returns {json} Returns results json , from condition based made calculation for 'pay extra per month' or 'years to payoff'
     */
    commonStudentLoanPayoffCalculation(results, amount, payment) {
        const originalTerm = results.term;
        let balanceAmount = amount;
        let months = 0;
        let totalInterest = 0;
        let expectedTotalInterest = 0;
        while (balanceAmount > 0 && months < results.maxMonths) {
            const currentInterest = balanceAmount * results.monthlyRate;
            totalInterest += currentInterest;
            const principalBalance = payment - currentInterest;
            balanceAmount = balanceAmount * (1 + results.monthlyRate) - payment;
            months++;
            results.months[months] = {
                principalBalance,
                currentInterest,
                balanceAmount,
                totalInterest,
            };
        }
        if (results.timeline === 0) {
            if (results.termInMonths > 0) {
                results.totalInterest = totalInterest;
                results.saved = results.expectedTotalInterest - totalInterest;
                results.newTotal = Math.round(results.basicTotal) - Math.round(results.saved);
                results.years = months / results.yearsTotalMonth;
            }
        } else {
            expectedTotalInterest = results.expectedTotalInterest;
            results.totalInterest = null;
            results.years = months / results.yearsTotalMonth;
            results.saved = expectedTotalInterest - totalInterest + balanceAmount;
            if (results.saved < 0) {
                results.saved = 0;
            }
            results.newTotal = Math.round(results.basicTotal) - Math.round(results.saved);
            results.desiredPayment = payment;
            results.monthlyPayment = null;
            results.expectedTotalInterest = null;
        }
        const resultsOfCalculation = {
            monthlyPayment: results.timeline === 0
                ? { old: results.payment, new: results.monthlyPayment, savings: -results.monthlyPayment + results.payment }
                : { old: results.payment, new: results.desiredPayment, savings: -results.desiredPayment + results.payment },
            interest: results.timeline === 0
                ? { old: results.expectedTotalInterest, new: totalInterest, savings: Math.round(results.expectedTotalInterest) - Math.round(totalInterest) }
                : { old: expectedTotalInterest, new: totalInterest, savings: Math.round(expectedTotalInterest) - Math.round(totalInterest) },
            balanceAmount:
                { old: results.basicTotal, new: results.newTotal, savings: Math.round(results.basicTotal) - Math.round(results.newTotal) },
            term: results.timeline === 0
                ? { old: originalTerm, new: results.years, savings: originalTerm - results.years }
                : { old: originalTerm, new: results.timeline, savings: parseFloat(originalTerm - results.timeline) },
        };
        return resultsOfCalculation;
    },
    /**
     * Based on intial input from user, calculate student pay off results using 'pay extra per month' option
     * invoked with one arguments: (results).
     *
     * @param {any} results made object that pass intial input from user and some intial parameter that need to pass for calculation.
     * @returns {json} Returns results json , for 'pay extra per month'
     */
    calculationAsPerPayExtra(results) {
        const { balanceAmount } = results;
        const monthlyPayment = results.payment + results.monthlyPayment;
        results.monthlyPayment = monthlyPayment;
        return this.commonStudentLoanPayoffCalculation(results, balanceAmount, monthlyPayment);
    },
    /**
     * Based on intial input from user, calculate student pay off results using 'years to payoff' option
     * invoked with one arguments: (results).
     *
     * @param {any} results made object that pass intial input from user and some intial parameter that need to pass for calculation.
     * @returns {json} Returns results json , for 'years to payoff'
     */
    calculationAsPerYeartoPayoff(results) {
        const { balanceAmount } = results;
        if (results.timeline > results.term) {
            return {
                monthlyPayment: { old: null, new: null, savings: null },
                interest: { old: null, new: null, savings: null },
                balanceAmount: { old: null, new: null, savings: null },
                term: { old: results.term, new: results.timeline, savings: null },
            };
        }
        const newTermInMonths = results.timeline * results.yearsTotalMonth;
        let desiredPayment = 0;
        let months;
        let desiredPaymentLow = results.payment;
        let desiredPaymentHigh;
        if (results.payment === 0) {
            desiredPaymentHigh = balanceAmount;
        } else {
            desiredPaymentHigh = results.tenUnits * results.payment;
        }
        while (desiredPaymentHigh > desiredPaymentLow + 0.01) {
            desiredPayment = (desiredPaymentLow + desiredPaymentHigh) / 2;
            months = 0;
            let balance = balanceAmount;
            while (balance > 0 && months < results.maxMonths) {
                balance = balance * (1 + results.monthlyRate) - desiredPayment;
                months++;
            }
            if (months < newTermInMonths) {
                desiredPaymentHigh = desiredPayment;
            } else {
                desiredPaymentLow = desiredPayment;
            }
        }
        return this.commonStudentLoanPayoffCalculation(results, balanceAmount, desiredPayment);
    },
    /**
     * Convert month and year decimal value in to month and year string format
     * invoked with one arguments: (monthYearValue, format).
     *
     * @param {any} monthYearValue from calculation get month and year value in decimal which convert in to format wise
     * @param {string} format format for convert year and month value, default long pass
     * @returns {string} Returns string , return converted year and month format wise
     */
    convertYearMonthString(monthYearValue, format = 'long') {
        let years = Math.floor(monthYearValue);
        let months = Math.round(monthYearValue % 1 * 12);
        let result = '';
        if (months === 12) {
            years += 1;
            months = 0;
        }
        if (format === 'long') {
            result = `${years} years`;
            if (months > 0.9) {
                result = `${result} ${months} months`;
            }
            return result;
        }
        if (format === 'medium') {
            const yearString = years > 1 ? 'yrs' : 'yr';
            result = `${years} ${yearString}`;
            if (months > 0.9) {
                result = `${result} ${months} mos`;
            }
            return result;
        }
        if (format === 'short') {
            result = `${years}yr`;
            if (months > 0.9) {
                result = `${result} ${months}mo`;
            }
            return result;
        }
        return result;
    },
};
